/**
 * Phase 17 — Admin Runbook.
 *
 * In-app onboarding/operations reference for HR admins. HR-admin only;
 * other roles redirect to /admin (which itself redirects them away).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useOutletContext } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import {
  BookOpen,
  ChevronRight,
  Hash,
  ExternalLink,
  FileText,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import AdminPageHeader from "./AdminPageHeader";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiErrorDetail } from "../../lib/api";

const SECTIONS = [
  {
    id: "first-time-setup",
    title: "1. First-time setup",
    body: `**Welcome.** Goodwin Resolve is the centralised case tracker for Employee Relations. Before you begin onboarding others, take ten minutes to verify your own account.

- Sign in with your administrator credentials. If you logged in with a temporary password, you'll be sent through **Change password** automatically. Pick something at least 10 characters with a letter, a number, and a symbol.
- Open **Profile → Display** and confirm your full name and email are accurate. These appear in audit rows and notifications.
- Open **Profile → Security**. We strongly recommend enabling MFA on your admin account. HR Admin and Compliance roles cannot disable MFA once enabled.
- Take a moment in **Settings → Notifications** to set your preferred channels.

If anything looks off, contact the system orchestrator before granting access to other users — every action you take is captured in a tamper-evident audit chain that you cannot rewrite later.`,
  },
  {
    id: "onboarding-hr-users",
    title: "2. Onboarding HR users (Bulk Invite)",
    body: `The fastest way to onboard your HR team is **Admin → Users → Bulk Invite**.

**CSV columns**

| Column | Required? | Notes |
| --- | --- | --- |
| \`email\` | yes | Lowercased on import. |
| \`full_name\` | yes | 2–120 characters. |
| \`role\` | yes | One of \`hr\`, \`manager\`, \`leadership\`, \`compliance\`. |
| \`service_line\` | required for hr & manager | Must match an active service-line code (e.g. \`GHA\`, \`GHBC\`, \`GLH\`, \`TVA\`, \`FH\`). |
| \`is_admin\` | optional | \`true\` only honoured when role=\`hr\`. |

You can grab a starter CSV from the **Download sample CSV** button in the dialog.

**Workflow**

1. Choose your CSV. Pick whether existing emails should be **skipped** (default — safest) or **updated** (also rotates their password).
2. Click **Preview import**. The dry-run shows how many rows will be created, updated, skipped, and any validation errors with row numbers.
3. Click **Import** to commit. You will see a credentials table for every newly-created user.
4. Click **Copy all** or **Download CSV** to save the temp passwords. *This is the only time these passwords appear* — they are never stored in readable form again.
5. Tick **I have stored these securely** and the dialog closes.

**Securely handing the password to the user**

Real email is currently mocked. The Email Outbox queue (\`Admin → Email Outbox\`) holds the rendered welcome email for each new user. Open the row, copy the body to your real email client (or pass the temp password through your existing secure handoff channel), then click **Mark as forwarded** so other admins can see the row is taken care of.`,
  },
  {
    id: "onboarding-team-members",
    title: "3. Onboarding team members",
    body: `Team members are not users — they're the employees that cases are *about*. Add them via **Admin → Users → Bulk Invite**? **No** — use **/team-members → Bulk Import** (HR Admin only).

**Required CSV columns**: \`position_id\` (preferred, alphanumeric e.g. \`7GA00001\`) **or** the legacy \`file_number\` (integer), plus \`full_name\`, \`department\`, \`service_line\`.
**Optional**: \`assigned_hr_partner_email\`, \`manager_name\`, \`manager_email\`, \`hire_date\` (YYYY-MM-DD).

The same two-phase preview/commit flow applies. On-conflict choices are:
- **skip** (default): existing position IDs are left untouched.
- **update**: matching rows are patched in place.

Service-line codes must match active rows from **Admin → Service Lines**. If you see a "is not an active service line code" error, head to that admin section first to enable or rename the code.`,
  },
  {
    id: "import-historical-cases",
    title: "4. Importing historical ER cases",
    body: `Use **Cases → Bulk Import (legacy)** to import cases from your old tracker. This flow is **for historical data only** — it intentionally bypasses the normal workflow rules so the imported case retains its original status and timeline.

Required columns: \`legacy_case_id\`, \`team_member_file_number\`, \`date_reported\`, \`issue_type\`, \`specific_issue\`, \`status\`. Optional fields like \`case_details\`, \`outcome\`, \`closure_summary\`, \`investigation_end_date\` follow the same names as the live model.

**Auto-create team members.** If a referenced \`team_member_file_number\` is not yet in the system, the importer creates a placeholder team member row so the case has a valid foreign key. Tidy these up afterwards.

**Provenance markers.** Every imported case is tagged \`imported: true\`, \`legacy_case_id\`, and \`import_batch_id\`. The Cases list shows an **Imported · Legacy** badge so reviewers know not to apply current-day SLAs.

**Dry-run first.** Always run with the preview toggle on and review the validation report before committing.`,
  },
  {
    id: "daily-monitoring",
    title: "5. Daily monitoring",
    body: `When you sign in, head to **Dashboard** for the day's pulse:

- **Open + In Progress + Under Investigation** counts at the top.
- **Pending Review queue** — leadership owes you a decision on these.
- **High-risk badge** — Harassment / Discrimination / Safety cases. Always review the same day.
- **Stale-case watch** — cases that haven't moved in over 14 days (configurable via \`Settings → SLA\`).

**Alerts to act on**:
1. Any high-risk case that is unassigned for more than 24 hours.
2. Any case auto-escalated by the SLA scheduler — these are the ones that drift past 30 days without resolution.
3. Pending Review entries older than 7 days (amber severity) or 14 days (red severity).

**Insights** (sidebar) gives a longitudinal view by service line, issue type, and HR partner. Use it for monthly reviews, not for daily firefighting.`,
  },
  {
    id: "reset-password",
    title: "6. Resetting a user's password",
    body: `From **Admin → Users**, find the user, hover the row, and click **Reset password**. Confirm in the dialog.

The system:
1. Generates a one-time temp password.
2. Replaces the user's hash with that temp password.
3. Forces \`must_change_password=true\` so the user is sent through the change flow on next sign-in.
4. Revokes every active refresh token for that user, and adds a sentinel that invalidates every still-issued access token within five minutes.
5. Writes a hash-chained audit row.

Hand off the temp password the same way you do for invites (Email Outbox or your secure handoff channel). The temp password is *only shown once* on screen and is not stored anywhere readable.`,
  },
  {
    id: "unlock-account",
    title: "7. Unlocking a locked account",
    body: `After 5 consecutive failed sign-in attempts on a single account, the user is locked for 15 minutes. The lock clears automatically — but if a user needs immediate access:

1. Open **Admin → Users**.
2. Locked users show a red **locked** pill in the Status column.
3. Hover the row and click **Unlock**.

This:
- Sets \`failed_login_attempts\` back to 0.
- Clears \`locked_until\`.
- Writes an \`account_unlocked\` audit row.

If the same user is locking themselves out repeatedly, consider running a **Reset password** instead — they likely don't have the right credential at all.`,
  },
  {
    id: "audit-log",
    title: "8. Reading the audit log",
    body: `**Admin → Audit Logs** is the canonical record of everything that happened in the system. HR Admin, Compliance, and Legal Review can read it; nobody can edit or delete entries.

Each row contains the action, the actor, the entity acted on, and the before/after diff for changes. The IP and short device fingerprint are included for HIPAA traceability.

**Chain integrity.** Every row is hash-chained — each entry includes the hash of the previous one. The banner at the top of the page shows whether the chain currently verifies. If it ever shows red, escalate immediately to security/orchestrator before performing further actions.

**Common queries**:
- Filter by entity_type=\`case\` to follow a single case end-to-end.
- Filter by action=\`legal_hold_set\` or \`case_archived\` for retention review.
- Filter by entity_type=\`email_outbox\` to see who forwarded which email when.`,
  },
  {
    id: "production-adapters",
    title: "9. Wire-up checklist for production-grade adapters",
    body: `When you are ready to move from mocks to real services, set the following env vars and restart the backend.

**Real email (SendGrid)**
- \`EMAIL_PROVIDER=sendgrid\`
- \`SENDGRID_API_KEY=SG.xxxxxxxxxxxx\`
- \`EMAIL_FROM=noreply@yourdomain.org\`
- \`EMAIL_FROM_NAME=Goodwin Resolve · Goodwin Living\`

The Email Outbox panel will auto-hide. Real deliveries go straight through SendGrid; \`email_outbox\` writes are skipped.

**Real email (Resend)**
- \`EMAIL_PROVIDER=resend\`
- \`RESEND_API_KEY=re_xxxxxxxxxxxx\`

**Document storage (Google Cloud Storage)**
- \`STORAGE_BACKEND=gcs\`
- \`GCS_BUCKET=your-bucket\`
- \`GCS_SERVICE_ACCOUNT_JSON=/secrets/sa.json\`

The GridFS adapter remains a tested fallback if GCS becomes unreachable.

**SSO (optional)**
- \`SSO_ENABLED=true\`
- \`SSO_PROVIDERS=okta\` (or \`google\`, etc.)
- Provider-specific client_id / secret / redirect_uri

After flipping any of these, head back to this Runbook and re-read each affected section to confirm the workflow expectations still apply.`,
  },
  {
    id: "emergency-contacts",
    title: "10. Emergency contacts",
    body: `Keep this list up to date. It exists so on-call admins know whom to wake up.

| Role | Name | Contact |
| --- | --- | --- |
| Privacy Officer | _add your privacy officer_ | _phone / email_ |
| Security & IT | _add your security lead_ | _phone / email_ |
| Legal | _add your legal counsel_ | _phone / email_ |
| Goodwin Resolve product owner | _add your product owner_ | _phone / email_ |

You can edit this section by editing \`/app/frontend/src/pages/admin/Runbook.jsx\` and redeploying. Treat that change like any other code change: PR, review, audit.`,
  },
];

export default function Runbook() {
  const { user } = useAuth();
  const [active, setActive] = useState(SECTIONS[0].id);

  // Highlight whichever section is currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
        );
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px" },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const isAdmin = user?.role === "hr" && user?.is_admin === true;
  if (!isAdmin) return <Navigate to="/admin" replace />;

  // Phase 17.x — download SOW + Executive Summary PDFs through the
  // authenticated axios client (so the JWT travels with the request),
  // then trigger a browser download from the blob.
  const downloadArtifact = async (path, filename) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 200);
      toast.success(`${filename} downloaded`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Download failed",
      );
    }
  };

  return (
    <div data-testid="admin-runbook-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · Runbook"
        title="Operations runbook"
        description="A condensed reference for everyday admin work — onboarding, monitoring, escalation, and the wire-up steps for real adapters."
        rightSlot={
          <span
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
            style={{
              backgroundColor: "rgba(94,171,69,0.10)",
              color: "#3F7E2D",
              border: "1px solid rgba(94,171,69,0.30)",
            }}
          >
            <BookOpen className="h-3 w-3" />
            HR Admin only
          </span>
        }
      />

      {/* Phase 17.x — downloadable artifacts (SOW + Executive Summary) */}
      <section
        data-testid="runbook-artifacts"
        className="brand-card flex flex-wrap items-center justify-between gap-3 px-4 py-3"
      >
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--page-fg-muted)]">
            Project artifacts
          </p>
          <p className="mt-0.5 text-sm text-[color:var(--page-fg-secondary)]">
            Hand off to clients, leadership, and external reviewers.
            Generated on demand from the live system.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="flex flex-col items-end gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadArtifact("/admin/sow.pdf", "Goodwin_Resolve_SOW_v2.pdf")}
              data-testid="runbook-download-sow"
              className="h-9"
            >
              <FileText className="mr-1.5 h-4 w-4" strokeWidth={1.6} />
              Download Scope of Work (PDF)
              <Download className="ml-1.5 h-3.5 w-3.5 opacity-70" strokeWidth={1.6} />
            </Button>
            <span
              data-testid="runbook-download-sow-version"
              className="text-[10px] font-medium tracking-wide text-[color:var(--page-fg-muted)]"
            >
              v2.0 · Updated {new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadArtifact("/admin/exec-summary.pdf", "Goodwin_Resolve_Executive_Summary.pdf")}
            data-testid="runbook-download-exec-summary"
            className="h-9"
          >
            <FileText className="mr-1.5 h-4 w-4" strokeWidth={1.6} />
            Download Executive Summary (PDF)
            <Download className="ml-1.5 h-3.5 w-3.5 opacity-70" strokeWidth={1.6} />
          </Button>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        {/* TOC */}
        <nav
          aria-label="Runbook table of contents"
          className="brand-card sticky top-4 h-fit p-3"
          data-testid="runbook-toc"
        >
          <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--page-fg-muted)]">
            Contents
          </p>
          <ul className="space-y-px">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  data-testid={`runbook-toc-${s.id}`}
                  className="group flex items-center gap-1.5 rounded-md px-2 py-1 text-[12.5px] transition-colors"
                  style={{
                    color: active === s.id ? "var(--page-fg)" : "var(--page-fg-secondary)",
                    backgroundColor: active === s.id ? "var(--surface-2)" : "transparent",
                    fontWeight: active === s.id ? 500 : 400,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                    setActive(s.id);
                  }}
                >
                  <ChevronRight
                    className="h-3 w-3 flex-shrink-0"
                    strokeWidth={2}
                    style={{ color: active === s.id ? "#5EAB45" : "var(--page-fg-muted)" }}
                  />
                  <span className="truncate">{s.title}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content */}
        <article className="space-y-8">
          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={s.id}
              data-testid={`runbook-section-${s.id}`}
              className="brand-card scroll-mt-24 p-6"
            >
              <header className="mb-3 flex items-center gap-2">
                <a
                  href={`#${s.id}`}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-[color:var(--surface-2)]"
                  aria-label={`Anchor for ${s.title}`}
                >
                  <Hash className="h-3.5 w-3.5 text-[color:var(--page-fg-muted)]" strokeWidth={1.6} />
                </a>
                <h2
                  className="font-serif text-xl font-semibold"
                  style={{ color: "var(--page-fg)" }}
                >
                  {s.title}
                </h2>
              </header>
              <div
                className="runbook-prose"
                style={{ color: "var(--page-fg-secondary)" }}
              >
                <ReactMarkdown
                  components={{
                    h3: ({ node, ...p }) => (
                      <h3
                        {...p}
                        className="mt-5 mb-2 font-serif text-base font-semibold"
                        style={{ color: "var(--page-fg)" }}
                      />
                    ),
                    p: ({ node, ...p }) => (
                      <p {...p} className="mb-3 text-sm leading-relaxed" />
                    ),
                    a: ({ node, ...p }) => (
                      <a
                        {...p}
                        className="inline-flex items-center gap-0.5 underline"
                        style={{ color: "#1E3A5F" }}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {p.children}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ),
                    ul: ({ node, ...p }) => (
                      <ul {...p} className="mb-3 ml-5 list-disc space-y-1 text-sm" />
                    ),
                    ol: ({ node, ...p }) => (
                      <ol {...p} className="mb-3 ml-5 list-decimal space-y-1 text-sm" />
                    ),
                    li: ({ node, ...p }) => <li {...p} className="leading-relaxed" />,
                    code: ({ node, inline, ...p }) =>
                      inline ? (
                        <code
                          {...p}
                          className="rounded bg-[color:var(--surface-2)] px-1.5 py-0.5 font-mono text-[12px]"
                          style={{ color: "var(--page-fg)" }}
                        />
                      ) : (
                        <code {...p} className="font-mono text-xs" />
                      ),
                    pre: ({ node, ...p }) => (
                      <pre
                        {...p}
                        className="my-3 overflow-auto rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs"
                      />
                    ),
                    strong: ({ node, ...p }) => (
                      <strong
                        {...p}
                        className="font-semibold"
                        style={{ color: "var(--page-fg)" }}
                      />
                    ),
                    table: ({ node, ...p }) => (
                      <div className="my-3 overflow-x-auto rounded-md border border-[color:var(--border-subtle)]">
                        <table
                          {...p}
                          className="w-full text-left text-sm"
                          style={{ color: "var(--page-fg-secondary)" }}
                        />
                      </div>
                    ),
                    thead: ({ node, ...p }) => (
                      <thead
                        {...p}
                        className="text-xs uppercase tracking-wider"
                        style={{
                          backgroundColor: "var(--surface-2)",
                          color: "var(--page-fg-muted)",
                        }}
                      />
                    ),
                    th: ({ node, ...p }) => <th {...p} className="px-3 py-2" />,
                    td: ({ node, ...p }) => <td {...p} className="border-t border-[color:var(--border-subtle)] px-3 py-2" />,
                  }}
                >
                  {s.body}
                </ReactMarkdown>
              </div>
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}
